import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { FiSend, FiX, FiBell } from 'react-icons/fi';
import api from '../api';

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [targetType, setTargetType] = useState('all'); // all, class, student
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'push', // push, alimtalk
    classGroupId: '',
    studentIds: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [notiRes, templatesRes, classesRes, studentsRes] = await Promise.all([
        api.get('/api/notifications'),
        api.get('/api/notifications/templates'),
        api.get('/api/class-groups', { params: { active: true } }),
        api.get('/api/users', { params: { role: 'student' } })
      ]);
      setNotifications(notiRes.data.notifications || []);
      setTemplates(templatesRes.data.templates || []);
      setClasses(classesRes.data.classGroups || []);
      setStudents(studentsRes.data.users || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    setFormData({
      title: '',
      message: '',
      type: 'push', // 푸시 알림 고정 (알림톡은 템플릿 승인 필요)
      classGroupId: '',
      studentIds: []
    });
    setTargetType('all');
    setShowModal(true);
  };

  const applyTemplate = (template) => {
    setFormData({
      ...formData,
      title: template.title,
      message: template.message,
      type: template.type
    });
  };

  // 선택된 반의 학생 목록 가져오기
  const getClassStudents = (classGroupId) => {
    if (!classGroupId) return [];
    const selectedClass = classes.find(c => c._id === classGroupId);
    return selectedClass?.students || [];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let recipients = [];
    if (targetType === 'all') {
      recipients = students.map(s => s._id);
    } else if (targetType === 'class') {
      // 선택한 반에 속한 학생 ID 배열 가져오기
      const classStudents = getClassStudents(formData.classGroupId);
      recipients = classStudents.map(s => s._id || s);
    } else {
      recipients = formData.studentIds;
    }

    if (recipients.length === 0) {
      alert('발송 대상이 없습니다');
      return;
    }

    if (!window.confirm(`${recipients.length}명에게 알림을 발송하시겠습니까?`)) return;

    try {
      await api.post('/api/notifications', {
        type: 'general',
        title: formData.title,
        body: formData.message,
        channel: formData.type,
        recipientUserIds: recipients
      });
      alert('발송되었습니다');
      setShowModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '발송에 실패했습니다');
    }
  };

  const toggleStudent = (studentId) => {
    const ids = formData.studentIds.includes(studentId)
      ? formData.studentIds.filter(id => id !== studentId)
      : [...formData.studentIds, studentId];
    setFormData({ ...formData, studentIds: ids });
  };

  // Stats
  const stats = {
    total: notifications.length,
    push: notifications.filter(n => n.channel === 'push').length,
    alimtalk: notifications.filter(n => n.channel === 'alimtalk').length,
    today: notifications.filter(n =>
      format(new Date(n.createdAt), 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
    ).length
  };

  // 알림 유형 레이블 매핑
  const getTypeLabel = (type) => {
    const typeMap = {
      'attendance': '출결',
      'lesson': '수업일지',
      'tuition': '수납',
      'general': '일반',
      'test_result': '성적',
      'schedule': '일정',
      'notice': '공지',
      'message': '메시지',
      'system': '시스템'
    };
    return typeMap[type] || type;
  };

  return (
    <div className="notifications-page">
      <div className="page-header">
        <h2>알림 발송</h2>
        <button className="btn btn-primary" onClick={openModal}>
          <FiSend /> 알림 발송
        </button>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">전체 발송</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.push}</div>
          <div className="stat-label">푸시 (10P)</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.alimtalk}</div>
          <div className="stat-label">알림톡 (30P)</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.today}</div>
          <div className="stat-label">오늘 발송</div>
        </div>
      </div>

      {/* Notification History */}
      <div className="table-container">
        <h3>발송 내역</h3>
        <table>
          <thead>
            <tr>
              <th>발송일시</th>
              <th>분류</th>
              <th>채널</th>
              <th>제목</th>
              <th>대상</th>
              <th>상태</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="loading">로딩 중...</td></tr>
            ) : notifications.length === 0 ? (
              <tr><td colSpan="6" className="empty">발송 내역이 없습니다</td></tr>
            ) : (
              notifications.map((noti) => (
                <tr key={noti._id}>
                  <td>{format(new Date(noti.createdAt), 'MM/dd HH:mm')}</td>
                  <td>
                    <span className="badge badge-info">
                      {getTypeLabel(noti.type)}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${noti.channel === 'push' || noti.channel === 'both' ? 'badge-primary' : 'badge-warning'}`}>
                      {noti.channel === 'push' ? '푸시' : noti.channel === 'alimtalk' ? '알림톡' : '푸시+알림톡'}
                    </span>
                  </td>
                  <td>{noti.title}</td>
                  <td>{noti.stats?.total || noti.recipients?.length || 1}명</td>
                  <td>
                    <span className={`status ${noti.sentAt ? 'active' : 'inactive'}`}>
                      {noti.sentAt ? '발송완료' : '대기'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Send Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>알림 발송</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              {/* Templates */}
              {templates.length > 0 && (
                <div className="form-group">
                  <label>템플릿 선택</label>
                  <div className="template-list">
                    {templates.map((t) => (
                      <button
                        key={t._id}
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => applyTemplate(t)}
                      >
                        {t.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>발송 대상 *</label>
                <select
                  value={targetType}
                  onChange={(e) => setTargetType(e.target.value)}
                >
                  <option value="all">전체 학생</option>
                  <option value="class">반 선택</option>
                  <option value="student">개별 선택</option>
                </select>
                <small className="text-muted" style={{ display: 'block', marginTop: '4px' }}>
                  ※ 알림 발송은 푸시 알림으로만 가능합니다 (10P/건)
                </small>
              </div>

              {targetType === 'class' && (
                <div className="form-group">
                  <label>반 선택 *</label>
                  <select
                    value={formData.classGroupId}
                    onChange={(e) => setFormData({ ...formData, classGroupId: e.target.value })}
                    required
                  >
                    <option value="">선택하세요</option>
                    {classes.filter(c => c.students?.length > 0).map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.name} ({c.students?.length || 0}명)
                      </option>
                    ))}
                  </select>
                  {classes.every(c => !c.students?.length) && (
                    <small className="text-muted">등록된 학생이 있는 반이 없습니다</small>
                  )}
                </div>
              )}

              {targetType === 'student' && (
                <div className="form-group">
                  <label>학생 선택 * ({formData.studentIds.length}명 선택됨)</label>
                  <div className="student-select-list">
                    {students.map((s) => (
                      <label key={s._id} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.studentIds.includes(s._id)}
                          onChange={() => toggleStudent(s._id)}
                        />
                        {s.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>제목 *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="알림 제목"
                  required
                />
              </div>
              <div className="form-group">
                <label>내용 *</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="알림 내용을 입력하세요"
                  rows={4}
                  required
                />
              </div>

              <div className="cost-preview">
                <FiBell />
                <span>
                  예상 비용: 10P × {targetType === 'all' ? students.length :
                   targetType === 'class' ? getClassStudents(formData.classGroupId).length :
                   formData.studentIds.length}명 = {10 * (targetType === 'all' ? students.length :
                   targetType === 'class' ? getClassStudents(formData.classGroupId).length :
                   formData.studentIds.length)}P
                </span>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  취소
                </button>
                <button type="submit" className="btn btn-primary">
                  <FiSend /> 발송
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
